import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { getCurrencySymbol } from '../../lib/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SECTION = { commercial: 'Commercial summary', subscription: 'Subscription', lines: 'Year-1 lines', terms: 'Terms', signatory: 'Signatories' };
const ORDER = ['commercial', 'subscription', 'lines', 'terms', 'signatory'];
const MONEY_FIELDS = /Implementation services|Subscription \(term total\)|Total contract value|Year \d+ subscription|unit rate|year 1 net|added|removed/;

function fmt(v, sym, isMoney) {
  if (v == null || v === '') return '—';
  if (typeof v === 'number') return isMoney ? `${sym}${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return String(v);
}

/** Side-by-side field changes between two quote versions of the same estimate (older → newer). */
export function QuoteCompareDialog({ open, onOpenChange, quote, quotes }) {
  const [withId, setWithId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const others = (quotes || []).filter((q) => q.id !== quote?.id);
  useEffect(() => { if (open) { setWithId(''); setData(null); } }, [open, quote?.id]);
  useEffect(() => {
    if (!open || !quote) return;
    setLoading(true);
    axios.get(`${API_URL}/api/quotes/${quote.id}/compare`, { params: withId ? { with_id: withId } : {}, withCredentials: true })
      .then((r) => setData(r.data)).catch((e) => setData({ error: e?.response?.data?.detail || 'Could not compare' })).finally(() => setLoading(false));
  }, [open, quote, withId]);
  const sym = getCurrencySymbol(data?.b?.currency || quote?.snapshot?.currency || 'AUD');
  const grouped = ORDER.map((s) => [s, (data?.changes || []).filter((c) => c.section === s)]).filter(([, rows]) => rows.length);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[85vh] overflow-y-auto" data-testid="quote-compare-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Compare quote versions</DialogTitle>
          <DialogDescription className="text-xs">
            {data?.a && data?.b ? <span data-testid="quote-compare-summary">{data.a.quote_number} (v{data.a.version} · {data.a.status}) → {data.b.quote_number} (v{data.b.version} · {data.b.status}) · {data.count} change{data.count === 1 ? '' : 's'}
              {data.tcv_delta != null && data.tcv_delta !== 0 && <span className={`ml-1 font-semibold ${data.tcv_delta > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>· TCV {data.tcv_delta > 0 ? '+' : '−'}{sym}{Math.abs(data.tcv_delta).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}</span>
              : 'Field-level differences in price, subscription lines and terms between two versions.'}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500">Compare {quote?.quote_number} with</span>
          <Select value={withId || 'prev'} onValueChange={(v) => setWithId(v === 'prev' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs w-72" data-testid="quote-compare-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="prev">Previous version</SelectItem>
              {others.map((q) => <SelectItem key={q.id} value={q.id} data-testid={`quote-compare-option-${q.quote_number}`}>{q.quote_number} · v{q.version} · {q.status}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {loading && <div className="flex items-center gap-2 text-xs text-slate-500 py-4"><Loader2 className="w-4 h-4 animate-spin" /> Comparing…</div>}
        {!loading && data?.error && <p className="text-xs text-rose-600 py-3" data-testid="quote-compare-error">{data.error}</p>}
        {!loading && data && !data.error && data.count === 0 && <p className="text-xs text-slate-500 py-3" data-testid="quote-compare-empty">No differences in price, subscription lines, terms or signatories between these versions.</p>}
        {!loading && grouped.length > 0 && (
          <table className="w-full text-xs" data-testid="quote-compare-table">
            <thead><tr className="text-[10px] uppercase text-slate-400 text-left border-b border-slate-200">
              <th className="py-1 pr-2 font-medium w-[34%]">Field</th><th className="py-1 pr-2 font-medium text-right">{data.a.quote_number} v{data.a.version}</th>
              <th className="py-1 pr-2 font-medium text-right">{data.b.quote_number} v{data.b.version}</th><th className="py-1 font-medium text-right">Δ</th>
            </tr></thead>
            <tbody>
              {grouped.map(([section, rows]) => (
                <React.Fragment key={section}>
                  <tr className="bg-slate-50"><td colSpan={4} className="py-1 px-1 text-[10px] uppercase tracking-wide font-semibold text-slate-500" data-testid={`quote-compare-section-${section}`}>{SECTION[section] || section}</td></tr>
                  {rows.map((c, i) => {
                    const money = c.delta != null || MONEY_FIELDS.test(c.field);
                    const longText = typeof c.from === 'string' && (c.from.length > 60 || String(c.to || '').length > 60);
                    return (
                      <tr key={`${section}-${i}`} className="border-b border-slate-100 align-top" data-testid={`quote-compare-row-${section}-${i}`}>
                        <td className="py-1 pr-2 font-medium text-[#0F2040]">{c.field}</td>
                        <td className={`py-1 pr-2 tabular-nums text-slate-500 ${longText ? 'text-left whitespace-pre-wrap' : 'text-right line-through'}`}>{fmt(c.from, sym, money)}</td>
                        <td className={`py-1 pr-2 tabular-nums font-semibold ${longText ? 'text-left whitespace-pre-wrap' : 'text-right'}`}>{fmt(c.to, sym, money)}</td>
                        <td className={`py-1 text-right tabular-nums ${c.delta > 0 ? 'text-emerald-700' : c.delta < 0 ? 'text-rose-700' : 'text-slate-400'}`}>{c.delta != null ? `${c.delta > 0 ? '+' : '−'}${sym}${Math.abs(c.delta).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : ''}</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
}
